import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { errorFromResponse } from '@/lib/errors';
import { uploadToS3 } from '@/lib/upload';

/**
 * QA-227 — Web FTP(고도몰 /share/popup_webftp.php 대체). catalog-service PR #61.
 * S3 버킷 files/ 아래만 다룬다. path 는 최상위 기준 상대 경로("" = Home).
 *   GET  /admin/v1/media/web-ftp?path=&keyword=     목록(폴더 먼저, 이름순) · 검색(현재 폴더, 대소문자 무시)
 *   POST /admin/v1/media/web-ftp/folders            { path, name } → 201
 *   POST /admin/v1/media/web-ftp/uploads            { path, files[{filename,sizeBytes}], overwrite } → items[](uploadUrl·method·headers·url)
 *        → 파일마다 S3 직접 PUT(별도 확정 없음, 5분 유효)
 *   POST /admin/v1/media/web-ftp/rename             { path, name, newName }
 *   POST /admin/v1/media/web-ftp/delete             { path, name } → 204 (폴더는 비었을 때만)
 *   GET  /admin/v1/media/web-ftp/download-url?path= → { downloadUrl } (첨부, 5분 유효)
 */
const BASE = '/admin/v1/media/web-ftp';

export type WebFtpEntryType = 'FOLDER' | 'FILE';

export interface WebFtpEntry {
  type: WebFtpEntryType;
  name: string;
  path: string;
  sizeBytes: number | null;
  lastModified: string | null;
  url: string;
}

export interface WebFtpListing {
  path: string;
  url: string;
  breadcrumbs: Array<{ name: string; path: string }>;
  items: WebFtpEntry[];
  /** 다음 묶음이 있으면 true(= nextCursor != null). */
  truncated: boolean;
  /** catalog #63 — 다음 묶음 cursor. 마지막이면 null. 옛 BE 는 보내지 않는다. */
  nextCursor?: string | null;
}

interface UploadPresignItem {
  filename: string;
  path: string;
  uploadUrl: string;
  method: string;
  headers: Record<string, string> | null;
  url: string;
}

interface Envelope<T> {
  data?: T | null;
}

const read = async <T,>(res: Response): Promise<T> => {
  if (!res.ok) throw await errorFromResponse(res);
  return ((await res.json()) as Envelope<T>).data as T;
};

const post = (path: string, body: unknown) =>
  adminFetch(`${BASE}${path}`, { method: 'POST', body: JSON.stringify(body) });

export const webFtpQueryKey = (path: string, keyword: string) => ['admin', 'web-ftp', path, keyword] as const;

/**
 * 한 묶음 조회. cursor 없이 = 첫 묶음(최대 1000개). catalog #63 — 다음 묶음은 이전 응답의 nextCursor 로.
 * cursor 는 그 시점 목록 기준이라, 폴더 이동·검색·업로드·이름 변경·삭제 뒤에는 처음부터 다시 받는다.
 */
export const fetchWebFtpPage = async (path: string, keyword: string, cursor?: string): Promise<WebFtpListing> => {
  const q = new URLSearchParams({ path });
  if (keyword.trim()) q.set('keyword', keyword.trim());
  if (cursor) q.set('cursor', cursor);
  return read<WebFtpListing>(await adminFetch(`${BASE}?${q.toString()}`));
};

/** 첫 묶음. [더 보기]로 받은 다음 묶음은 화면이 따로 이어 붙인다(첫 묶음이 새로 오면 비운다). */
export const useWebFtpListQuery = (path: string, keyword: string) =>
  useQuery<WebFtpListing>({
    queryKey: webFtpQueryKey(path, keyword),
    placeholderData: keepPreviousData,
    queryFn: () => fetchWebFtpPage(path, keyword),
  });

/** 파일 여러 개를 현재 폴더로 — 업로드 주소를 받은 뒤 S3 로 직접 올린다. 같은 이름이면 BE 가 409(overwrite 로 재요청). */
export const uploadWebFtpFiles = async (path: string, files: File[], overwrite: boolean): Promise<void> => {
  const presign = await read<{ items: UploadPresignItem[] }>(
    await post('/uploads', {
      path,
      files: files.map((f) => ({ filename: f.name, sizeBytes: f.size })),
      overwrite,
    }),
  );
  const byName = new Map(files.map((f) => [f.name, f] as const));
  await uploadToS3(
    presign.items.map((it) => ({
      item: {
        fileId: it.path,
        objectKey: it.path,
        uploadUrl: it.uploadUrl,
        uploadMethod: 'PUT' as const,
        requiredHeaders: it.headers ?? {},
        publicUrl: it.url,
      },
      file: byName.get(it.filename) as File,
    })),
  );
};

export const useWebFtpMutations = () => {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'web-ftp'] });

  const createFolder = useMutation({
    mutationFn: async (body: { path: string; name: string }) => read<WebFtpEntry>(await post('/folders', body)),
    onSuccess: invalidate,
  });
  const upload = useMutation({
    mutationFn: ({ path, files, overwrite }: { path: string; files: File[]; overwrite: boolean }) =>
      uploadWebFtpFiles(path, files, overwrite),
    onSettled: invalidate,
  });
  const rename = useMutation({
    mutationFn: async (body: { path: string; name: string; newName: string }) =>
      read<WebFtpEntry>(await post('/rename', body)),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: async (body: { path: string; name: string }) => {
      const res = await post('/delete', body);
      if (!res.ok) throw await errorFromResponse(res);
    },
    onSuccess: invalidate,
  });
  return { createFolder, upload, rename, remove };
};

export const fetchWebFtpDownloadUrl = async (path: string): Promise<string> => {
  const q = new URLSearchParams({ path });
  return (await read<{ downloadUrl: string }>(await adminFetch(`${BASE}/download-url?${q.toString()}`))).downloadUrl;
};

/** 고도몰과 같은 이름 규칙 — 영문·숫자·. _ - 만, 첫 글자는 영문/숫자, 100자 이하. */
export const WEB_FTP_NAME_RULE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,99}$/;
export const isValidWebFtpName = (name: string): boolean => WEB_FTP_NAME_RULE.test(name) && !name.includes('..');

export const WEB_FTP_MAX_BYTES = 20 * 1024 * 1024;
export const WEB_FTP_EXTENSIONS = [
  'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'ico',
  'html', 'htm', 'css', 'js',
  'eot', 'woff', 'woff2', 'ttf', 'otf',
] as const;
export const webFtpExtensionOk = (name: string): boolean =>
  (WEB_FTP_EXTENSIONS as ReadonlyArray<string>).includes(name.split('.').pop()?.toLowerCase() ?? '');
